import { css } from "lit";

// Shared component styles, implementing the "Bino Workspace" design language
// (__INTERNAL/design_handoff_bino_registry). Every class reads design tokens via
// var(--…) so theming is global and zero-JS (N2). Components opt in with
// `static styles = [shared, css`…`]`. Class names are kept stable so the dozen
// smaller components re-theme to the new palette without per-component edits.
export const shared = css`
  :host {
    display: block;
    color: var(--text);
    font-family: var(--sans);
  }
  a {
    color: var(--brand-ink);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  .muted {
    color: var(--text-dim);
  }
  .faint {
    color: var(--text-faint);
  }
  .mono {
    font-family: var(--mono);
  }
  /* Section eyebrow — Space Grotesk, per the design type scale. */
  .eyebrow {
    font-family: var(--display);
    font-size: var(--fs-4);
    font-weight: 600;
    line-height: 1;
  }
  h1,
  h2,
  h3 {
    margin: 0 0 var(--sp-2);
    line-height: 1.2;
    font-weight: 600;
  }
  h1 {
    font-size: var(--fs-5);
  }
  h2 {
    font-size: var(--fs-4);
  }
  h3 {
    font-size: var(--fs-3);
  }

  /* Centered content wrappers used by the simpler routed pages. */
  .wrap {
    max-width: 1240px;
    margin: 0 auto;
    padding: 28px 24px 80px;
  }
  .wrap-narrow {
    max-width: 760px;
    margin: 0 auto;
    padding: 28px 24px 80px;
  }

  .card {
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--surface);
    padding: var(--sp-4);
    margin-bottom: var(--sp-4);
  }

  /* Tag/keyword chip — small square-cornered pill (design "Tags"). */
  .chip {
    display: inline-flex;
    align-items: center;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 5px;
    padding: 3px 7px;
    font-size: 10.5px;
    font-weight: 500;
    line-height: 1;
    margin: 2px 4px 2px 0;
    color: var(--text-dim);
  }

  /* Kind label — monospace pink-ink pill. */
  .kind {
    display: inline-flex;
    align-items: center;
    font-family: var(--mono);
    font-size: 10.5px;
    font-weight: 500;
    line-height: 1;
    color: var(--accent-ink);
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 5px;
    padding: 3px 7px;
  }

  /* Buttons */
  .btn {
    font: inherit;
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    padding: 8px 13px;
    border-radius: var(--radius);
    border: 1px solid var(--border-strong);
    background: var(--surface);
    color: var(--text);
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .btn:hover {
    border-color: var(--brand);
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn-primary {
    background: var(--brand);
    border-color: var(--brand);
    color: var(--brand-contrast);
  }
  .btn-primary:hover {
    border-color: var(--brand);
    filter: brightness(1.03);
  }
  .btn-danger {
    border-color: var(--neg);
    color: var(--neg);
    background: var(--surface);
  }

  /* Form controls */
  input,
  select,
  textarea {
    font: inherit;
    font-family: var(--sans);
    font-size: var(--fs-2);
    padding: 8px 11px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--surface-2);
    color: var(--text);
    width: 100%;
  }
  input:focus,
  select:focus,
  textarea:focus {
    border-color: var(--brand);
    outline: none;
  }
  input::placeholder,
  textarea::placeholder {
    color: var(--text-faint);
  }
  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-dim);
    margin-bottom: var(--sp-1);
  }

  /* Tables (version history, etc.) */
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-2);
  }
  th,
  td {
    text-align: left;
    padding: 9px 10px;
    border-bottom: 1px solid var(--border);
  }
  thead th {
    background: var(--surface-2);
  }
  th {
    /* text-dim (not the design's faint) for AA contrast on the surface-2 header. */
    color: var(--text-dim);
    font-family: var(--mono);
    font-weight: 600;
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* Fixed near-black code block (install commands, YAML) — both themes. */
  pre {
    font-family: var(--mono);
    background: var(--code-bg);
    color: var(--code-fg);
    border: 0;
    border-radius: 9px;
    padding: 15px 17px;
    overflow: auto;
    font-size: 12.5px;
    line-height: 1.6;
    margin: 0;
  }

  .badge-stable {
    color: var(--brand-ink);
    font-size: var(--fs-1);
    font-weight: 600;
  }
  .badge-deprecated {
    color: var(--warning);
    font-size: var(--fs-1);
    font-weight: 600;
  }
  .badge-yanked {
    color: var(--neg);
    font-size: var(--fs-1);
    font-weight: 600;
  }

  .error {
    color: var(--neg);
    background: color-mix(in srgb, var(--neg) 10%, transparent);
    border: 1px solid var(--neg);
    border-radius: var(--radius);
    padding: var(--sp-2) var(--sp-3);
    font-size: var(--fs-2);
    margin: var(--sp-3) 0;
  }
  .field {
    margin-bottom: var(--sp-4);
  }

  /* Wrapper for wide tables so they pan on phones instead of overflowing the page. */
  .table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 720px) {
    .wrap,
    .wrap-narrow {
      padding: 20px 16px 64px;
    }
    /* 16px keeps iOS Safari from auto-zooming focused inputs (base is 14px). */
    input,
    select,
    textarea {
      font-size: 16px;
    }
  }
`;

// Centered auth card used by page-login / page-signup / page-welcome, mirroring
// the cloud.bino.bi login design: brand mark, headline + lede, full-width
// actions, "or" separator and underlined alt links. Layered after `shared`
// (static styles = [shared, authStyles, …]) so its :host/h1 rules win.
export const authStyles = css`
  :host {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: clamp(1.5rem, 8vh, 6rem) var(--sp-4) var(--sp-6);
  }
  .auth-card {
    width: 100%;
    max-width: 25rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: var(--sp-6);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  }
  .mark {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: var(--sp-5);
  }
  .mark .glyph {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: var(--brand);
    color: var(--brand-contrast);
    display: flex;
    align-items: center;
    justify-content: center;
    font: 800 18px/1 var(--display);
  }
  .mark .word {
    font: 600 15px/1 var(--sans);
    color: var(--text);
  }
  h1 {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0 0 4px;
  }
  .lede {
    margin: 0 0 var(--sp-5);
    color: var(--text-dim);
    font-size: var(--fs-2);
    line-height: 1.5;
  }
  .lede b {
    color: var(--text);
    font-weight: 500;
    word-break: break-all;
  }
  .code-input {
    font-family: var(--mono);
    font-size: var(--fs-4);
    letter-spacing: 0.35em;
    text-align: center;
  }
  .actions {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }
  .actions .btn {
    width: 100%;
    padding: 10px 13px;
  }
  .sso-sep {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: var(--sp-4) 0;
    color: var(--text-faint);
    font-size: var(--fs-1);
  }
  .sso-sep::before,
  .sso-sep::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .alt {
    margin: var(--sp-4) 0 0;
    text-align: center;
    font-size: 13px;
    color: var(--text-dim);
  }
  .alt button,
  .alt a {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--text-dim);
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  .alt button:hover,
  .alt a:hover {
    color: var(--text);
  }
  .alt button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
